import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { ShieldCheck } from "lucide-react";
import { fetchPermissions, updateRolePermissions } from "../../redux/actionCreator";
import "./permissionsPage.scss";

/**
 * Role × permission matrix.
 *
 * super_admin is intentionally absent from the table — the middleware
 * short-circuits it to always-allow so editing its row would be a no-op
 * and a footgun (a "saved" empty row would still grant everything via
 * the bypass, surprising whoever reads the matrix later).
 *
 * Changes accumulate in `dirty` (per role) and only flush to the
 * backend on Save. The page calls `fetchPermissions` once on mount;
 * the backend caches role rows in memory so the matrix paints fast
 * even with many roles.
 */
export default function PermissionsPage() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [savingRole, setSavingRole] = useState(null);
  const [err, setErr] = useState(null);
  const [catalog, setCatalog] = useState({ groups: [], keys: [] });
  // role → Set<permission>. We work in a Set in the component so
  // toggling a checkbox is O(1) and the diff vs server is trivial.
  const [matrix, setMatrix] = useState({});      // committed state from server
  const [pending, setPending] = useState({});    // role → Set after edits
  const [meta, setMeta] = useState({});          // role → { stale, seeded, updated_at, last_updated_by }

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    const res = await dispatch(fetchPermissions());
    setLoading(false);
    if (!res?.status) { setErr(res?.message || "Failed to load permissions"); return; }
    const d = res.data || {};
    setCatalog(d.catalog || { groups: [], keys: [] });
    const m = {};
    const p = {};
    const mt = {};
    for (const r of d.roles || []) {
      const set = new Set(r.permissions || []);
      m[r.role] = set;
      p[r.role] = new Set(set);
      mt[r.role] = { stale: r.stale || [], seeded: !!r.seeded, updated_at: r.updated_at, last_updated_by: r.last_updated_by };
    }
    setMatrix(m); setPending(p); setMeta(mt);
  }, [dispatch]);
  useEffect(() => { load(); }, [load]);

  const toggle = (role, key) => {
    setPending((cur) => {
      const next = { ...cur };
      const set = new Set(next[role] || []);
      if (set.has(key)) set.delete(key); else set.add(key);
      next[role] = set;
      return next;
    });
  };

  const toggleGroup = (role, keys, allOn) => {
    setPending((cur) => {
      const next = { ...cur };
      const set = new Set(next[role] || []);
      for (const k of keys) { if (allOn) set.delete(k); else set.add(k); }
      next[role] = set;
      return next;
    });
  };

  const isDirty = (role) => {
    const a = matrix[role] || new Set();
    const b = pending[role] || new Set();
    if (a.size !== b.size) return true;
    for (const x of a) if (!b.has(x)) return true;
    return false;
  };

  const save = async (role) => {
    setSavingRole(role);
    const list = [...(pending[role] || [])];
    const res = await dispatch(updateRolePermissions(role, list));
    setSavingRole(null);
    if (!res?.status) { alert(res?.message || "Failed to save"); return; }
    // Commit pending → matrix and clear the "seeded" badge (we now
    // have a real row on the server side).
    setMatrix((cur) => ({ ...cur, [role]: new Set(list) }));
    setMeta((cur) => ({ ...cur, [role]: { ...(cur[role] || {}), seeded: false, updated_at: new Date().toISOString() } }));
  };

  const roles = useMemo(() => Object.keys(pending), [pending]);

  if (loading) {
    return <div className="permissions-page"><div className="text-center py-5"><Spinner animation="border" /></div></div>;
  }

  if (err) {
    return (
      <div className="permissions-page">
        <div className="alert alert-danger m-3">
          <strong>{err}</strong>
          <div className="small mt-1">Only users with <code>permissions.manage</code> (default: super_admin) can open this page.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="permissions-page">
      <div className="ph-head">
        <div>
          <h4 className="m-0 d-inline-flex align-items-center gap-2">
            <ShieldCheck size={20} /> Role permissions
          </h4>
          <small className="text-muted">
            Toggle what each role can do. <strong>super_admin</strong> bypasses every check and isn't editable here.
          </small>
        </div>
        <div className="d-flex gap-2">
          <Button size="sm" variant="light" onClick={load} disabled={loading}>↻</Button>
        </div>
      </div>

      <div className="ph-roles">
        {roles.map((role) => {
          const granted = pending[role] || new Set();
          const dirty = isDirty(role);
          const m = meta[role] || {};
          return (
            <div key={role} className={`ph-role-card ${dirty ? "is-dirty" : ""}`}>
              <div className="ph-role-head">
                <div>
                  <h5 className="m-0">{role}</h5>
                  <small className="text-muted">
                    {granted.size} permission{granted.size === 1 ? "" : "s"} enabled
                    {m.seeded && <span className="badge bg-info ms-2" title="Using catalog defaults — never saved">defaults</span>}
                    {m.stale?.length > 0 && (
                      <span className="badge bg-warning text-dark ms-2" title={m.stale.join(", ")}>
                        {m.stale.length} stale key{m.stale.length === 1 ? "" : "s"}
                      </span>
                    )}
                  </small>
                </div>
                <div className="d-flex gap-2 align-items-center">
                  {dirty && <span className="badge bg-warning text-dark">unsaved</span>}
                  <Button
                    size="sm" variant={dirty ? "primary" : "outline-secondary"}
                    disabled={!dirty || savingRole === role}
                    onClick={() => save(role)}
                  >
                    {savingRole === role ? <Spinner size="sm" animation="border" /> : "Save"}
                  </Button>
                </div>
              </div>

              <div className="ph-groups">
                {(catalog.groups || []).map((g) => {
                  const keys = g.keys || [];
                  const onCount = keys.filter((k) => granted.has(k)).length;
                  const allOn = onCount === keys.length && keys.length > 0;
                  const someOn = onCount > 0 && !allOn;
                  return (
                    <div key={g.title} className="ph-group">
                      <div className="ph-group-head">
                        <Form.Check
                          type="checkbox"
                          id={`group-${role}-${g.title}`}
                          label={g.title}
                          checked={allOn}
                          ref={(el) => { if (el) el.indeterminate = someOn; }}
                          onChange={() => toggleGroup(role, keys, allOn)}
                        />
                        <span className="ph-group-count">{onCount}/{keys.length}</span>
                      </div>
                      <div className="ph-perm-list">
                        {keys.map((k) => (
                          <Form.Check
                            key={k}
                            type="checkbox"
                            id={`perm-${role}-${k}`}
                            label={<code>{k}</code>}
                            checked={granted.has(k)}
                            onChange={() => toggle(role, k)}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="text-muted small mt-3">
        Changes flush instantly to the cache on the API server — subsequent requests
        from this role's users are gated by the new set immediately. No restart needed.
      </div>
    </div>
  );
}
